import {
  Customer,
  CustomerUpdateData,
  ICustomer,
} from '../../model/customer';
import {
  checkForNullProperties,
  checkNumberFormat,
} from '../../shared/services/validation.service';
import {
  ICustomerDetailsDto,
  ICustomerSummaryResDto,
  IUpdateCustomerReqDto,
  IUpdateCustomerResDto,
} from '../dto/customer.dto';

export function fromCustomerDetailsDtoMapper(
  dto: ICustomerDetailsDto
): ICustomer {
  const customerData = Customer.fromJSON({
    createdAt: dto.data.customer.createdAt,
    storeId: dto.data.customer.storeId,
    id: dto.data.customer.id,
    taxId: dto.data.customer.taxId,
    note: dto.data.customer.note,
    firstName: dto.data.customer.contactInfo.firstName,
    lastName: dto.data.customer.contactInfo.lastName,
    company: dto.data.customer.contactInfo.company,
    zipCode: dto.data.customer.contactInfo.zipCode,
    province: dto.data.customer.contactInfo.province,
    city: dto.data.customer.contactInfo.city,
    district: dto.data.customer.contactInfo.district,
    barangay: dto.data.customer.contactInfo.barangay,
    unitFloorRoomBuildingNo: dto.data.customer.contactInfo.unitFloorRoomBuildingNo,
    buildingNameTower: dto.data.customer.contactInfo.buildingNameTower,
    lotBlockPhaseHouseNo: dto.data.customer.contactInfo.lotBlockPhaseHouseNo,
    street: dto.data.customer.contactInfo.street,
    subdivisionVillageZone: dto.data.customer.contactInfo.subdivisionVillageZone,
    birthday: dto.data.customer.contactInfo.birthday,
    email: dto.data.customer.contactInfo.email,
    mobile: checkNumberFormat(dto.data.customer.contactInfo.mobile),
    telephone: checkNumberFormat(dto.data.customer.contactInfo.telephone),

    // Mapping statistics
    totalTransactions: dto.data.statistics.transactionCount,
    totalAmountSpent: dto.data.statistics.totalAmountSpent,
    lastTransactionTimestamp: dto.data.statistics.lastTransactionTimestamp,
    averageAmountSpentPerTransaction:
      dto.data.statistics.averageTransactionValue,

    // Mapping product, categories, and transactions
    products: checkForNullProperties(dto.data.top10BestSellers.products),
    categories: checkForNullProperties(
      dto.data.transactionCountsByCategories.categories
    ),
    transactions: checkForNullProperties(
      dto.data.transactionHistory.transactions
    ),

    // Solo parent fields
    childName: dto.data.customer.childName,
    childBirthDate: dto.data.customer.childBirthDate,
  });
  return customerData;
}

export function fromCustomerSummaryDtoMapper(
  customer: ICustomerSummaryResDto
): ICustomer {
  return Customer.fromJSON({
    storeId: customer.storeId!,
    id: customer.id,
    firstName: customer.firstName,
    lastName: customer.lastName,
    company: customer.company,
    taxId: customer.taxId,
    zipCode: customer.zipCode,
    province: customer.province,
    city: customer.city,
    district: customer.district,
    barangay: customer.barangay,
    unitFloorRoomBuildingNo: customer.unitFloorRoomBuildingNo,
    buildingNameTower: customer.buildingNameTower,
    lotBlockPhaseHouseNo: customer.lotBlockPhaseHouseNo,
    street: customer.street,
    subdivisionVillageZone: customer.subdivisionVillageZone,
    birthday: customer.birthday ? new Date(customer.birthday) : null,
    mobile: customer.mobile,
    telephone: customer.telephone,
    email: customer.email,
    note: customer.note,
    totalTransactions: customer.transactionCount,
    totalAmountSpent: customer.totalSpentAmount,
    lastTransactionId: customer.lastTransactionId,
    
    // Solo parent fields
    childName: customer.childName,
    childBirthDate: customer.childBirthDate,
  });
}

export function toUpdateCustomerDtoMapper(
  customer: CustomerUpdateData
): IUpdateCustomerReqDto {
  return {
    storeId: customer.storeId!,
    firstName: customer.firstName!,
    lastName: customer.lastName!,
    company: customer.company,
    zipCode: customer.zipCode,
    province: customer.province,
    city: customer.city,
    district: customer.district,
    barangay: customer.barangay,
    unitFloorRoomBuildingNo: customer.unitFloorRoomBuildingNo,
    buildingNameTower: customer.buildingNameTower,
    lotBlockPhaseHouseNo: customer.lotBlockPhaseHouseNo,
    street: customer.street,
    subdivisionVillageZone: customer.subdivisionVillageZone,
    birthday: customer.birthday,
    email: customer.email,
    mobile: checkNumberFormat(customer.mobile),
    telephone: checkNumberFormat(customer.telephone),
    taxId: customer.taxId,
    note: customer.note,
    
    // Solo parent fields
    childName: customer.childName,
    childBirthDate: customer.childBirthDate,
  };
}

export function fromUpdateCustomerDtoMapper(
  customer: IUpdateCustomerResDto
): Partial<ICustomer> {
  return {
    storeId: customer.storeId!,
    id: customer.id,
    taxId: customer.taxId,
    note: customer.note,
    firstName: customer.contactInfo.firstName,
    lastName: customer.contactInfo.lastName,
    company: customer.contactInfo.company,
    zipCode: customer.contactInfo.zipCode,
    province: customer.contactInfo.province,
    city: customer.contactInfo.city,
    district: customer.contactInfo.district,
    barangay: customer.contactInfo.barangay,
    unitFloorRoomBuildingNo: customer.contactInfo.unitFloorRoomBuildingNo,
    buildingNameTower: customer.contactInfo.buildingNameTower,
    lotBlockPhaseHouseNo: customer.contactInfo.lotBlockPhaseHouseNo,
    street: customer.contactInfo.street,
    subdivisionVillageZone: customer.contactInfo.subdivisionVillageZone,
    birthday: customer.contactInfo.birthday
      ? new Date(customer.contactInfo.birthday)
      : null,
    email: customer.contactInfo.email,
    mobile: customer.contactInfo.mobile,
    telephone: customer.contactInfo.telephone,
    
    // Solo parent fields
    childName: customer.childName,
    childBirthDate: customer.childBirthDate,
  };
}
